import { z } from "zod";

// 상품 목록 조회 응답
export const ShopProductSchema = z.object({
  id: z.number(),
  thumbnail: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  price: z.number(),
});

export type ShopProduct = z.infer<typeof ShopProductSchema>;

export const ShopProductListResponseSchema = z.object({
  data: z.array(ShopProductSchema),
  errorMsg: z.string().nullable(),
  errorCode: z.string().nullable(),
});

export type ShopProductListResponse = z.infer<
  typeof ShopProductListResponseSchema
>;

// 상품 상세 조회 응답
export const PriceTierSchema = z.object({
  id: z.number(),
  minQuantity: z.number(),
  price: z.number(),
});

export const DetailPageImageSchema = z.object({
  id: z.number(),
  imageUrl: z.string(),
  displayOrder: z.number(),
});

export const ShopProductDetailSchema = z.object({
  id: z.number(),
  companyId: z.number(),
  categoryId: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  thumbnail: z.string().nullable(),
  detailPage: z.array(DetailPageImageSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
  isBestSeller: z.boolean(),
  priceTiers: z.array(PriceTierSchema),
});

export type ShopProductDetail = z.infer<typeof ShopProductDetailSchema>;

export const ShopProductDetailResponseSchema = z.object({
  data: ShopProductDetailSchema,
  errorMsg: z.string().nullable(),
  errorCode: z.string().nullable(),
});

export type ShopProductDetailResponse = z.infer<
  typeof ShopProductDetailResponseSchema
>;

// 트리거 타입 정의
export const TriggerTypeSchema = z.enum(["AND", "OR", "NAND", "NOR"]);
export type TriggerType = z.infer<typeof TriggerTypeSchema>;

export const TriggerVisibleTypeSchema = z.enum(["HIDE", "DISABLE"]);
export type TriggerVisibleType = z.infer<typeof TriggerVisibleTypeSchema>;

// 옵션 관련 타입
export const SampleValueSchema = z.object({
  id: z.number(),
  imageUrl: z.string().nullable(),
  value: z.string(),
  description: z.string().nullable(),
  height: z.number().nullable(),
  width: z.number().nullable(),
  displayOrder: z.number(),
  constraints: z.any().nullable(),
  // 트리거 관련 필드
  triggerAttributeValueIds: z.array(z.number()).nullable().optional(),
  triggerType: TriggerTypeSchema.nullable().optional(),
  triggerVisibleType: TriggerVisibleTypeSchema.nullable().optional(),
});

export type SampleValue = z.infer<typeof SampleValueSchema>;

export const AttributeSchema: z.ZodType<any> = z.lazy(() =>
  z.object({
    id: z.number(),
    name: z.string(),
    description: z.string().nullable(),
    parentValueId: z.number().nullable(),
    attributeType: z.enum([
      "SELECT",
      "TEXT",
      "NUMBER",
      "ENUM",
      "SIZE",
      "GRAMMAGE",
    ]),
    previewType: z.enum([
      "TEXT",
      "IMAGE",
      "COLOR",
      "SELECT_BOX",
      "SELECT_BOX_WITH_IMAGE",
      "DROP_DOWN_MENU",
      "INPUT_BOX",
    ]),
    displayOrder: z.number(),
    isRequiredAttribute: z.boolean(),
    sampleValues: z.array(SampleValueSchema).nullable(),
    childAttributes: z.array(AttributeSchema).nullable(),
    // 트리거 관련 필드
    triggerAttributeValueIds: z.array(z.number()).nullable().optional(),
    triggerType: TriggerTypeSchema.nullable().optional(),
    triggerVisibleType: TriggerVisibleTypeSchema.nullable().optional(),
  }),
);

export type Attribute = {
  id: number;
  name: string;
  description: string | null;
  parentValueId: number | null;
  attributeType: "SELECT" | "TEXT" | "NUMBER" | "ENUM" | "SIZE" | "GRAMMAGE";
  previewType:
    | "TEXT"
    | "IMAGE"
    | "COLOR"
    | "SELECT_BOX"
    | "SELECT_BOX_WITH_IMAGE"
    | "DROP_DOWN_MENU"
    | "INPUT_BOX";
  displayOrder: number;
  isRequiredAttribute: boolean;
  sampleValues: SampleValue[] | null;
  childAttributes: Attribute[] | null;
  // 트리거 관련 필드
  triggerAttributeValueIds?: number[] | null;
  triggerType?: TriggerType | null;
  triggerVisibleType?: TriggerVisibleType | null;
};

export const OptionSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  quantityApplyType: z.enum([
    "PER_ITEM",
    "PER_ORDER",
    "SAME_AS_PRODUCT_QUANTITY",
    "SELECTABLE",
  ]),
  isMultiOption: z.boolean(),
  isRequiredOption: z.boolean(),
  isBasedOnSizeOption: z.boolean(),
  displayOrder: z.number(),
  attributes: z.array(AttributeSchema),
});

export type Option = z.infer<typeof OptionSchema>;

export const CombinationSchema = z.object({
  id: z.number(),
  isRequiredCombination: z.boolean(),
  attributeIds: z.array(z.number()),
  excludedAttributeIds: z.array(z.number()),
  combinationKeys: z.array(z.string()),
});

export type Combination = z.infer<typeof CombinationSchema>;

export const ProductOptionResponseSchema = z.object({
  productId: z.number(),
  combinations: z.array(CombinationSchema),
  options: z.array(OptionSchema),
  width: z.number().nullable(),
  height: z.number().nullable(),
  spineWidth: z.number().nullable(),
  pageCount: z.number().nullable(),
});

export type ProductOptionResponse = z.infer<typeof ProductOptionResponseSchema>;

export const ProductOptionApiResponseSchema = z.object({
  data: ProductOptionResponseSchema,
  errorMsg: z.string().nullable(),
  errorCode: z.string().nullable(),
});

export type ProductOptionApiResponse = z.infer<
  typeof ProductOptionApiResponseSchema
>;

// 옵션 조회 요청
export type AttributeIdRequest = {
  attributeId: number;
  value?: string;
  sampleValueId?: number;
};

export type CombinationRequest = {
  combinationId: number;
  attributeIds: AttributeIdRequest[];
  selectableCount?: number;
};

export type ProductOptionRequest = {
  productId: number;
  productCount: number;
  combinations: CombinationRequest[];
};

// 가격 계산 관련
export const OptionPriceSchema = z.object({
  optionName: z.string(),
  size: z.string().nullable(),
  optionQuantity: z.number(),
  optionPrice: z.number(),
  grammage: z.number().nullable().optional(),  // 평량 (g/m²) - 용지 옵션인 경우에만 값이 있음
});

export type OptionPrice = z.infer<typeof OptionPriceSchema>;

export const CalculatePriceResponseSchema = z.object({
  productName: z.string(),
  productQuantity: z.number(),
  productPrice: z.number(),
  optionPrices: z.array(OptionPriceSchema),
  vatAmount: z.number(),
  calculateAmount: z.number(),
  totalAmount: z.number(),
});

export type CalculatePriceResponse = z.infer<
  typeof CalculatePriceResponseSchema
>;

export const CalculatePriceApiResponseSchema = z.object({
  data: CalculatePriceResponseSchema,
  errorMsg: z.string().nullable(),
  errorCode: z.string().nullable(),
});

export type CalculatePriceApiResponse = z.infer<
  typeof CalculatePriceApiResponseSchema
>;

export const ProductEntitySchema = z.object({
  id: z.number(),
  companyId: z.number(),
  categoryId: z.number(),
  name: z.string(),
  description: z.string().optional().nullable(),
  thumbnail: z.string().optional().nullable(),
  detailPage: z.array(DetailPageImageSchema).optional().nullable(),
  createdAt: z.string(),
  updatedAt: z.string().optional().nullable(),
  isBestSeller: z.boolean(),
  priceTiers: z.array(PriceTierSchema).optional().nullable(),
});

export type ProductEntity = z.infer<typeof ProductEntitySchema>;
